import React from 'react';
import { RADII, FONT_SIZE, FONTS } from '../../../theme';

interface ControllerVisualizerProps {
  connectedGamepadName?: string | null;
}

const CONTROLLER_CALLOUTS = [
  { group: 'Movement', items: ['Left Stick / D-Pad: Move Left, Right, Climb', 'South (A / ✕): Jump / Confirm'] },
  { group: 'Combat', items: ['West (X / □): Basic Attack', 'East (B / ○): Secondary Attack / Back', 'Bumpers and triggers (LB, RB, LT, RT): bar 1 slots 1 to 4 unless rebound above'] },
  { group: 'Interaction & Menus', items: ['North (Y / △): Interact / Loot', 'Start / Options: Contextual Quest Accept & Turn-In', 'Back / Select / Share: Quick Menu'] },
];

export const ControllerVisualizer: React.FC<ControllerVisualizerProps> = ({
  connectedGamepadName,
}) => {
  return (
    <div style={containerStyle} data-testid="controller-visualizer">
      {/* Header / Connected Status */}
      <div style={headerStyle}>
        <div style={titleStyle}>🎮 Gamepad Mapping (Standard W3C Layout)</div>
        <div style={statusBadgeStyle}>
          {connectedGamepadName ? (
            <span style={{ color: '#2ecc71' }}>● Connected: {connectedGamepadName}</span>
          ) : (
            <span style={{ color: 'var(--tome-ink-muted, #6b5a43)' }}>○ Standard Gamepad Supported</span>
          )}
        </div>
      </div>

      {/* Controller Callout Grid */}
      <div style={gridStyle}>
        {CONTROLLER_CALLOUTS.map((cat) => (
          <div key={cat.group} style={cardStyle}>
            <div style={groupTitleStyle}>{cat.group}</div>
            <ul style={listStyle}>
              {cat.items.map((item, idx) => (
                <li key={idx} style={itemStyle}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '12px 14px',
  background: 'linear-gradient(180deg, rgba(246, 238, 218, 0.78), rgba(232, 218, 188, 0.78))',
  borderRadius: RADII.md,
  border: '1px solid var(--tome-rule-strong, rgba(92, 64, 28, 0.38))',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 1px 3px rgba(70, 48, 14, 0.1)',
  marginBottom: 10,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid var(--tome-rule, rgba(92, 64, 28, 0.2))',
  paddingBottom: 6,
};

const titleStyle: React.CSSProperties = {
  fontFamily: FONTS.ui,
  fontSize: FONT_SIZE.xs,
  color: 'var(--tome-ochre, #8f560f)',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

const statusBadgeStyle: React.CSSProperties = {
  fontFamily: FONTS.ui,
  fontSize: 11,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 8,
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(255, 252, 240, 0.62)',
  border: '1px solid var(--tome-rule-strong, rgba(92, 64, 28, 0.35))',
  borderRadius: RADII.sm,
  padding: '8px 10px',
};

const groupTitleStyle: React.CSSProperties = {
  fontFamily: FONTS.ui,
  fontSize: 11,
  color: 'var(--tome-ochre, #8f560f)',
  fontWeight: 'bold',
  marginBottom: 4,
};

const listStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 14,
  fontFamily: FONTS.ui,
  fontSize: 11,
  color: 'var(--tome-ink, #24180c)',
  lineHeight: 1.5,
};

const itemStyle: React.CSSProperties = {
  marginBottom: 2,
};
